import { useEffect, useRef, useState } from 'react';
import { auth as authApi } from '@/api/endpoints';
import { registerStepUpHandler } from '@/api/client';
import { describe } from '@/lib/errors';
import { useAuth } from '@/stores/auth';

/**
 * Re-authentication prompt for dangerous actions.
 *
 * The API client raises this when an endpoint answers 403 STEP_UP_REQUIRED,
 * then retries the original request once a token is obtained. Mounting this
 * component is what makes that retry possible, so it lives in the layout and
 * every caller of a dangerous endpoint gets the flow for free.
 */
export function StepUpDialog() {
  const mfaEnabled = useAuth((s) => s.user?.mfa_enabled ?? false);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const resolver = useRef<((token: string | null) => void) | null>(null);
  const firstField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    registerStepUpHandler(
      () =>
        new Promise<string | null>((resolve) => {
          resolver.current = resolve;
          setOpen(true);
        }),
    );
    return () => registerStepUpHandler(null);
  }, []);

  useEffect(() => {
    if (open) firstField.current?.focus();
  }, [open]);

  function finish(token: string | null) {
    resolver.current?.(token);
    resolver.current = null;
    setOpen(false);
    setPassword('');
    setCode('');
    setError(null);
  }

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await authApi.stepUp(password, code || undefined);
      finish('granted');
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-labelledby="step-up-title">
      <div className="panel dialog">
        <div className="panel-head">
          <h2 id="step-up-title">Confirm it's you</h2>
        </div>
        <div className="panel-body" style={{ display: 'grid', gap: 'var(--space-4)' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
            This action changes credentials, risk limits, or live trading. Enter your password
            again to continue.
          </p>
          {error && <div className="field-error">{error}</div>}
          <div>
            <label htmlFor="step-up-password">Password</label>
            <input
              id="step-up-password"
              ref={firstField}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          {mfaEnabled && (
            <div>
              <label htmlFor="step-up-code">Authentication code</label>
              <input
                id="step-up-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => finish(null)} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="primary" onClick={confirm} disabled={busy || !password}>
              {busy ? 'Checking…' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
